import {Card, Spinner} from 'react-bootstrap'
import {DASHBOARD_SCALE, retentionStyle} from './dashboardColors'

interface ICohortMatrixRow {
  effectiveYear: number
  effectiveMonth: number
  monthLabel: string
  totalPolicies: number
  activeCounts: number[]  // index k = # with approved commission at eff_month+k (0–12)
}

interface Props {
  rows: ICohortMatrixRow[]
  loading: boolean
  compact?: boolean
}

const MAX_OFFSET = 11


export const CohortMatrixTable = ({rows, loading, compact}: Props) => {
  if (loading) {
    return (
      <Card className='card-flush'>
        <Card.Body className='d-flex justify-content-center align-items-center' style={{minHeight: 120}}>
          <Spinner animation='border' size='sm' />
        </Card.Body>
      </Card>
    )
  }

  if (!rows || rows.length === 0) return null

  const offsets = Array.from({length: MAX_OFFSET + 1}, (_, i) => i)

  // Totals row: for each offset k, only include cohorts that can reach k within their plan year.
  // Jan (eff_month=1) can reach offsets 0–11; Feb 0–10; … Dec 0–0.
  // Using all cohorts as the denominator for M+11 would inflate it with months that can
  // never have a commission at that offset — making the total meaningless.
  const activeTotals = offsets.map((k) => rows.reduce((s, r) => s + (r.activeCounts[k] ?? 0), 0))
  const eligibleTotals = offsets.map((k) =>
    rows
      .filter((r) => k <= MAX_OFFSET - (r.effectiveMonth - 1))
      .reduce((s, r) => s + r.totalPolicies, 0)
  )
  const totalAll = rows.reduce((s, r) => s + r.totalPolicies, 0)
  // A totals column is "all blank" if every row marks it as beyond their plan year
  const colAllBlank = offsets.map((k) => rows.every((r) => k > MAX_OFFSET - (r.effectiveMonth - 1)))

  const Legend = () => (
    <span className='text-muted fs-8'>
      {DASHBOARD_SCALE.map((t, i) => (
        <span
          key={i}
          className='badge ms-1'
          style={{backgroundColor: t.bg, color: t.text}}
        >
          {i === DASHBOARD_SCALE.length - 1 ? '<50%' : `≥${t.min}%`}
        </span>
      ))}
    </span>
  )

  return (
    <Card className='card-flush'>
      <Card.Header className={compact ? 'pt-3 pb-2' : 'pt-5'}>
        <Card.Title className='fw-bold text-dark fs-6'>Cohort Retention Matrix</Card.Title>
        <div className='card-toolbar'>
          {compact ? (
            <Legend />
          ) : (
            <span className='text-muted fs-8'>
              Each cell shows policies still active (approved commission) N months after effective date.
              Color: <Legend />
            </span>
          )}
        </div>
      </Card.Header>
      <Card.Body className='p-0' style={{overflowX: 'auto'}}>
        <table className='table table-bordered table-sm mb-0 fs-8' style={{minWidth: 700}}>
          <thead className='table-light'>
            <tr>
              <th className='text-nowrap ps-4 pe-3' style={{minWidth: 100}}>Cohort</th>
              <th className='text-center text-muted' style={{minWidth: 54}}>Total</th>
              {offsets.map((k) => (
                <th key={k} className='text-center' style={{minWidth: 54}}>
                  M+{k}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const maxOffset = MAX_OFFSET - (row.effectiveMonth - 1)  // Jan→12, Dec→0
              return (
                <tr key={`${row.effectiveYear}-${row.effectiveMonth}`}>
                  <td className='fw-bold text-nowrap ps-4 pe-3'>{row.monthLabel}</td>
                  <td className='text-center text-muted'>{row.totalPolicies}</td>
                  {offsets.map((k) => {
                    if (k > maxOffset) {
                      return <td key={k} className='text-center text-muted bg-light'>—</td>
                    }
                    const count = row.activeCounts[k] ?? 0
                    const pct = row.totalPolicies > 0 ? (count / row.totalPolicies) * 100 : 0
                    const style = retentionStyle(pct)
                    return (
                      <td
                        key={k}
                        className='text-center'
                        style={style ?? {color: '#a1a5b7'}}
                        title={`${count} / ${row.totalPolicies} (${pct.toFixed(1)}%)`}
                      >
                        {count > 0 ? `${pct.toFixed(0)}%` : '—'}
                      </td>
                    )
                  })}
                </tr>
              )
            })}
          </tbody>
          <tfoot>
            <tr className='table-secondary fw-bold'>
              <td className='ps-4 pe-3 text-nowrap'>Total</td>
              <td className='text-center'>{totalAll}</td>
              {offsets.map((k) => {
                if (colAllBlank[k]) {
                  return <td key={k} className='text-center text-muted'>—</td>
                }
                const count = activeTotals[k]
                const eligible = eligibleTotals[k]
                const pct = eligible > 0 ? (count / eligible) * 100 : 0
                const style = retentionStyle(pct)
                return (
                  <td
                    key={k}
                    className='text-center'
                    style={style ?? {color: '#a1a5b7'}}
                    title={`${count} / ${eligible} eligible (${pct.toFixed(1)}%)`}
                  >
                    {count > 0 ? `${pct.toFixed(0)}%` : '—'}
                  </td>
                )
              })}
            </tr>
          </tfoot>
        </table>
      </Card.Body>
    </Card>
  )
}
